import { supabase } from "./supabase";

export type AttendanceBundleStudent = {
  id: string;
  name: string;
  class_name?: string | null;
  /**
   * Rufname / nickname from `profile_info_student.nickname`. Strictly
   * nullable — most students don't have one. Display formatters should
   * prefer `nickname ?? name`.
   */
  nickname?: string | null;
};

export type AttendanceBundleBadgeCounts = {
  total_students: number;
  present: number;
  late: number;
  absent: number;
};

export type AttendanceBundleEditPermission = {
  can_edit: boolean;
  is_my_lesson: boolean;
  is_within_week: boolean;
  has_existing_attendance: boolean;
};

export type ConcurrentCourseStudent = {
  id: string;
  name: string;
  status: string | null; // null = no attendance yet, 'present', 'late', 'absent_excused', etc.
  is_guest?: boolean;
};

export type ConcurrentCourse = {
  lesson_id: string;
  course_name: string;
  students: ConcurrentCourseStudent[];
};

export type ConcurrentMainLesson = {
  lesson_id: string;
  lesson_name: string;
  students_with_main_attendance: ConcurrentCourseStudent[];
} | null;

export type ConcurrentCourseAttendance = {
  courses?: ConcurrentCourse[];
  main_lesson?: ConcurrentMainLesson;
};

export type KBOverride = {
  student_id: string;
  expected_start_time: string;
  expected_end_time: string;
};

export type GuestElsewhere = {
  student_id: string;
  name: string;
  other_course_id: string;
  other_course_name: string;
  guest_reason: string | null;
};

export type AttendanceBundle = {
  lesson: any | null;
  students: AttendanceBundleStudent[];
  attendance: { present: any[]; late: any[]; absent: any[] };
  badge_counts: AttendanceBundleBadgeCounts | null;
  recorder_meta: {
    recorded_by: string | null;
    name: string | null;
    at: string | null;
  } | null;
  diary: {
    text: string | null;
    /**
     * Hausaufgaben for the lesson, from `lesson_diary_entries.homework`.
     * Surfaced so the AttendanceDialog can prefill its inline
     * Hausaufgaben field on reopen. Strictly nullable — most lessons
     * don't have homework set.
     */
    homework?: string | null;
    created_at: string | null;
    created_by: string | null;
    created_by_name: string | null;
    is_course_short_notice_cancelled?: boolean;
  } | null;
  prefills: {
    krank: string[];
    daily_absent_excused: string[];
    daily_absent_unexcused: string[];
    /** Daily-log presence_status='absent_unclear' (pre-Klärung). Added
     *  alongside the bundle RPC fix on 2026-05-19 so the loader can
     *  prefill kids whose status was set in a past lesson when the
     *  intermediate lesson doesn't have its own SAL row. */
    daily_absent_unclear: string[];
    daily_present: string[];
    daily_late: { student_id: string; expected_arrival_time?: string | null }[];
    previous_lesson_status: {
      student_id: string;
      status: string;
      notes?: string | null;
    }[];
  };
  protections: {
    class_absence_overlap: boolean;
    checked_out: string[];
  };
  edit_permission: AttendanceBundleEditPermission;
  is_betreuung: boolean;
  has_areas: boolean;
  concurrent_course_attendance: ConcurrentCourseAttendance;
  kb_overrides: KBOverride[];
  next_lesson_courses: Array<{
    course_name: string;
    room_name?: string;
    teacher_names?: string;
    is_cancelled?: boolean;
    students: Array<{ id: string; name: string }>;
  }>;
  next_lesson_is_cancelled: boolean;
  next_lesson_subject: string | null;
  /** Class-distribution guest students for this lesson (folded in from get_attendance_bundle_v2). */
  guests: Array<{
    student_id: string;
    first_name: string | null;
    last_name: string | null;
    source_class_id: string | null;
    source_class_name: string | null;
    source_label: string | null;
    source_class_absence_id: string | null;
    source_course_lesson_id?: string | null;
  }>;
  guests_elsewhere: GuestElsewhere[];
  /**
   * Student IDs with a `kind='transferred_back'` row in
   * `lesson_guest_students` for this lesson. The home class loader uses
   * this set to un-exclude them from the regular roster so they appear as
   * Ausstehend (the home teacher decides whether they made it back).
   */
  pending_transfer_back_ids?: string[];
  /**
   * Student IDs the COURSE lesson pushed back to its main class as pending
   * (i.e. `lesson_guest_students` rows with
   * `source_course_lesson_id = this lesson` and `kind='transferred_back'`).
   * The course loader uses this set to exclude them from the course roster
   * so they aren't re-prefilled as present from `daily_present`.
   * Empty for class lessons.
   */
  transferred_away_student_ids?: string[];
  /** True iff the bundle's class has a band assignment matching this
   *  lesson's day_of_week + period. Lets the dialog skip mounting
   *  BandLogEntryForm (and its 4 RPC queries) on lessons with no band. */
  has_band_assignment: boolean;
  /**
   * All band data the BandLogEntryForm needs for THIS lesson's class+date.
   * Populated server-side by `fn_attendance_bundle_band_data` so the form
   * can read everything from props instead of running its own 4 RPC calls.
   * Shape mirrors the legacy RPC outputs (bands, assignments, periods, logs).
   */
  band_data?: {
    bands: any[];
    assignments: any[];
    periods: any[];
    logs: any[];
  };
};

export type AttendanceBundleFlags = {
  include_next_lesson?: boolean;
  include_kb_overrides?: boolean;
  include_recorder_meta?: boolean;
};

/**
 * Consolidated RPC for AttendanceDialog - replaces ~38 network calls with 1
 */
export async function getAttendanceBundle(
  lessonId: string,
  schoolId: string,
  userId?: string | null,
  flags?: AttendanceBundleFlags,
): Promise<AttendanceBundle> {
  const { data, error } = await supabase.rpc("get_attendance_bundle_v2", {
    p_lesson_id: lessonId,
    p_user_id: userId || null,
    p_flags: flags || null,
  });
  if (error) throw error;
  const d = (data || {}) as any;
  return {
    lesson: d.lesson || null,
    students: Array.isArray(d.students)
      ? d.students.map((s: any) => ({
          id: s.id,
          name: s.name,
          class_name: s.class_name ?? null,
          // Explicit pass-through so a future refactor to a strict whitelist
          // mapper doesn't silently drop the Rufname. Strictly nullable —
          // most students don't have one.
          nickname: s.nickname ?? null,
        }))
      : [],
    attendance: {
      present: Array.isArray(d.attendance?.present) ? d.attendance.present : [],
      late: Array.isArray(d.attendance?.late) ? d.attendance.late : [],
      absent: Array.isArray(d.attendance?.absent) ? d.attendance.absent : [],
    },
    badge_counts: d.badge_counts || null,
    recorder_meta: d.recorder_meta || null,
    diary: d.diary || null,
    prefills: {
      krank: Array.isArray(d.prefills?.krank) ? d.prefills.krank : [],
      daily_absent_excused: Array.isArray(d.prefills?.daily_absent_excused)
        ? d.prefills.daily_absent_excused
        : [],
      daily_absent_unexcused: Array.isArray(d.prefills?.daily_absent_unexcused)
        ? d.prefills.daily_absent_unexcused
        : [],
      daily_absent_unclear: Array.isArray(d.prefills?.daily_absent_unclear)
        ? d.prefills.daily_absent_unclear
        : [],
      daily_present: Array.isArray(d.prefills?.daily_present)
        ? d.prefills.daily_present
        : [],
      daily_late: Array.isArray(d.prefills?.daily_late)
        ? d.prefills.daily_late
        : [],
      previous_lesson_status: Array.isArray(d.prefills?.previous_lesson_status)
        ? d.prefills.previous_lesson_status
        : [],
    },
    protections: {
      class_absence_overlap: !!d.protections?.class_absence_overlap,
      checked_out: Array.isArray(d.protections?.checked_out)
        ? d.protections.checked_out
        : [],
    },
    edit_permission: d.edit_permission || {
      can_edit: false,
      is_my_lesson: false,
      is_within_week: false,
      has_existing_attendance: false,
    },
    is_betreuung: !!d.is_betreuung,
    has_areas: !!d.has_areas,
    concurrent_course_attendance: d.concurrent_course_attendance || {
      courses: [],
    },
    kb_overrides: Array.isArray(d.kb_overrides) ? d.kb_overrides : [],
    next_lesson_courses: Array.isArray(d.next_lesson_courses) ? d.next_lesson_courses : [],
    next_lesson_is_cancelled: !!d.next_lesson_is_cancelled,
    next_lesson_subject: d.next_lesson_subject || null,
    guests: Array.isArray(d.guests) ? d.guests : [],
    pending_transfer_back_ids: Array.isArray(d.pending_transfer_back_ids)
      ? d.pending_transfer_back_ids
      : [],
    transferred_away_student_ids: Array.isArray(d.transferred_away_student_ids)
      ? d.transferred_away_student_ids
      : [],
    guests_elsewhere: Array.isArray(d.guests_elsewhere) ? d.guests_elsewhere : [],
    // Forwarded from the bundle RPC so EditSection can gate BandLogEntryForm
    // mounting (and skip its 4 RPC queries) when the lesson has no band.
    has_band_assignment: !!d.has_band_assignment,
    // Inline band datasets so the form reads from props instead of firing
    // its own 4 RPCs.
    band_data: d.band_data
      ? {
          bands: Array.isArray(d.band_data.bands) ? d.band_data.bands : [],
          assignments: Array.isArray(d.band_data.assignments)
            ? d.band_data.assignments
            : [],
          periods: Array.isArray(d.band_data.periods) ? d.band_data.periods : [],
          logs: Array.isArray(d.band_data.logs) ? d.band_data.logs : [],
        }
      : undefined,
  } as AttendanceBundle;
}

// ─── Carry-forward gating ──────────────────────────────────────────────

/**
 * True when a lesson's attendance was GENUINELY recorded by a person —
 * the only reliable signal being a non-null recorder.
 *
 * `student_attendance_logs.recorded_by` is set when a teacher saves
 * attendance (AttendanceDialog / inline "Bestätigen" → the
 * save_lesson_attendance_bulk RPC). Auto/system logs leave it NULL:
 *   - a day-level absence (krank) propagated onto every lesson, and
 *   - a kiosk / pre-Klärung `absent_unclear` row.
 * Those must NOT make a lesson look "already recorded" — otherwise the
 * previous-lesson carry-forward prefill is wrongly suppressed for the
 * whole class.
 *
 * The bundle's `recorder_meta.recorded_by` is the latest recorder
 * across the lesson's logs, so it is null exactly when no genuine
 * recording exists.
 */
export function hasGenuineAttendance(
  recorderMeta: { recorded_by?: string | null } | null | undefined,
): boolean {
  return !!recorderMeta?.recorded_by;
}
